import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    ActivationCostColumn,
    ActivationDateColumn,
    AgentOwnerColumn,
    IccidColumn,
    IdColumn,
    NumberColumn,
    ReasonColumn,
    StatusColumn,
} from './components';
import {
    Box,
    Button,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import {
    getMessageGroups,
    getSimcardEvents,
    getSimcards,
} from '../../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import AddSimCardModal from './modals/AddSimCardModal';
import { DateTimeColumn } from '../shortlinks/components';
import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { MantineReactTable } from 'mantine-react-table';
import ResourceEventsModal from '../../../modals/events/EventsModal';
import { StatColumn } from '../../../common';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const SimcardsView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        detailsModalOpen,
        { open: openDetailsModal, close: closeDetailsModal },
    ] = useDisclosure();

    const [
        addSimCardModalOpen,
        { open: openAddSimCardModal, close: closeAddSimCardModal },
    ] = useDisclosure();

    const [modalTab, setModalTab] = useState('events');
    const [modalItem, setModalItem] = useState(null);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getSimcards(queryParams),
        queryKey: ['simcards'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: simcards, pagination: dataPagination } = paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            IccidColumn(),
            NumberColumn(),
            StatusColumn(),
            //AgentOwnerColumn(),
            StatColumn('Sent', (x) => x.stats?.success),
            StatColumn(
                'Failed',
                (x) => sumObjectKeys(x.stats, ['failed']),
                false
            ),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const sends = sumObjectKeys(x.stats, ['success']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / sends) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const conversions = sumObjectKeys(x.stats, ['conversion']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((conversions / uniqueClicks) * 100, 2);
                },
                true
            ),
            ActivationCostColumn(),
            ActivationDateColumn({
                id: 'activatedAt',
                header: 'Activated At',
                accessorFn: (rowData) => rowData.activatedAt,
                format: 'MM/DD/YYYY',
            }),
            ReasonColumn(),
        ],
        [simcards]
    );

    const tableData = useMemo(
        () => (isLoading ? [] : simcards),
        [simcards, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'simcards-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'simcards-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search SIM Cards',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <Box
                w={'100%'}
                px={'lg'}>
                <Group justify={'space-between'}>
                    <FiltersMultiSelect
                        options={[{ label: 'test', value: 'test' }]}
                    />
                    <Button
                        size={'compact-xs'}
                        onClick={openAddSimCardModal}>
                        Add Sim Card
                    </Button>
                </Group>
            </Box>
        ),
        renderRowActions: (props) => (
            <ActionsColumn
                {...props}
                setTab={setModalTab}
                setModalItem={setModalItem}
                openDetailsModal={openDetailsModal}
            />
        ),

        manualPagination: true,
        manualSorting: true,
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,

        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <ResourceEventsModal
                title={'Simcard Events'}
                httpQueryFn={(queryParams) =>
                    getSimcardEvents({
                        id: modalItem?.id,
                        pagination: queryParams,
                    })
                }
                queryKey={['simcard_events', modalItem?.id]}
                openTab={modalTab}
                opened={detailsModalOpen}
                close={closeDetailsModal}
            />
            <AddSimCardModal
                opened={addSimCardModalOpen}
                close={closeAddSimCardModal}
            />
        </Container>
    );
};
export default SimcardsView;
